import * as React from "react";
import cbsa from "util/cbsa";
import states from "util/states";
import { getOneYearsBack, toLocalDate } from "../utils/commonFunctions";
import counties from "util/counties";

const initial = {
  fileName: "",

  providerType: "",
  npis: "",
  timeFrameDays: "",
  timeFrameDirection: "",
  codeType: "",
  reportType: "",
  codeInput: "",

  payerMix: "",
  startDate: getOneYearsBack(),
  endDate: new Date(),
  medicareRefDate: null,
  comboRefDate: null,
  startDate2: getOneYearsBack(),
  endDate2: new Date(),

  geographyType: "",
  zipCodes: "",
  cbsaSelection: null,
  stateSelection: null,
  patientZip3s: "",

  countiesSelection: [],

  includeFacilities: false,
  npis2: "",

  canBeVisualized: true,

  outputs: {
    npi: true,
    name: true,
    classification: true,
    specialization: true,
    icd10Cm: true,
    msdrg: true,
    cptHcpcs: true,
    address: true,
    city: true,
    zipCode: true,
    source: true,
    distinctClaimsCount: true,
    distinctPatientsCount: true,
    sumCharges: true,
    hospitalAffiliationIds: true,
    hospitalAffiliationNames: true,
    groupPracticeIds: true,
    groupPracticeNames: true,
    year: true,
    quarter: true,
    month: true,
    stateName: true,
    county: true,
    proceduresCount: true,
    sourceNpi: false,
    sourceName: false,
    outputCode: true,
    codeServiceLine: true,
    codeDescription: true,
    placeOfService: true,
    locationNpi: true,
    locationName: true,
    locationClassification: true,
    locationSpecialization: true,
    billingNpi: true,
    billingName: true,
    billingClassification: true,
    billingSpecialization: true,
    outputCodeType: true,
    codeSet: true,
    renderingNpi: true,
    renderingName: true,
    renderingClassification: true,
    renderingSpecialization: true,
    renderingState: true,
    renderingCity: true,
    renderingZipCode: true,
    billingState: true,
    billingCity: true,
    billingZipCode: true,
    fipsCode: true,
    fipsCountyName: true,
    fipsState: true,
    firstNPI: true,
    firstPhysicianName: true,
    firstPhysicianClassification: true,
    firstPhysicianSpecialization: true,
    newPatients: true,
    secondNPI: true,
    secondPhysicianName: true,
    secondPhysicianClassification: true,
    secondPhysicianSpecialization: true,
    timeframe: true,
    procedureCode: true,
    procedureDescription: true,
    sharedPatients: true,
    firstOrgNPI: true,
    firstOrgName: true,
    firstOrgClassification: true,
    firstOrgSpecialization: true,
    secondOrgNPI: true,
    secondOrgName: true,
    secondOrgClassification: true,
    secondOrgSpecialization: true
  },

  // edit mode reference
  priorQueryParams: null,
};

function deepClone(o) { return structuredClone(o); }

function reducer(state, action) {
  switch (action.type) {
    case "SET": {
      const { key, value } = action;
      return { ...state, [key]: value };
    }
    case "SET_OUTPUT": {
      const { key, value } = action;
      return { ...state, outputs: { ...state.outputs, [key]: !!value } };
    }
    case "BATCH": {
      return { ...state, ...action.payload };
    }
    case "RESET_COMMON": {
      const reset = deepClone(initial);

      reset.payerMix = state.payerMix;

      return {
        ...state,
        ...reset,
        priorQueryParams: state.priorQueryParams, // <- preserve this
      };
    }
    default:
      return state;
  }
}


export function useCommonStateFields(opts = {}) {

  const { overrides = {}, enableAutoClear = true } = opts;
  const [state, dispatch] = React.useReducer(reducer, { ...deepClone(initial), ...overrides });
  const set = React.useCallback((key, value) => dispatch({ type: "SET", key, value }), []);
  const setOutput = React.useCallback((key, value) => dispatch({ type: "SET_OUTPUT", key, value }), []);
  const batch = React.useCallback((payload) => dispatch({ type: "BATCH", payload }), []);
  const resetCommon = React.useCallback(() => dispatch({ type: "RESET_COMMON" }), []);

  const bind = React.useMemo(() => ({
    text: (key) => ({
      value: state[key] ?? "",
      onChange: (e) => set(key, e.target.value),
    }),
    select: (key) => ({
      value: state[key] ?? "",
      onChange: (e) => set(key, e.target.value),
    }),
    checkbox: (key) => ({
      checked: !!state[key],
      onChange: (e) => set(key, !!e.target.checked),
    }),
    checkboxOutput: (key) => ({
      checked: !!state.outputs[key],
      onChange: (e) => setOutput(key, !!e.target.checked),
    }),
    date: (key) => ({
      selected: state[key] ?? null,
      onChange: (date) => set(key, date),
    }),
  }), [state, set, setOutput]);

  const selectors = {
    isCommercial: state.payerMix === "commercial",
    isMedicare: state.payerMix === "medicare",
    isAllPayer: state.payerMix === "commercial-and-medicare",
  };

  const getFirstStartDate = p => {
    if (p?.start_date) return toLocalDate(p.start_date);
    else if (p?.start_date_first) return toLocalDate(p.start_date_first);
    else return state?.startDate;
  };

  const getFirstEndDate = p => {
    if (p?.end_date) return toLocalDate(p.end_date);
    else if (p?.end_date_first) return toLocalDate(p.end_date_first);
    else return state?.endDate;
  };

  const getSecondStartDate = p => {
    if (p?.measure_start_date) return toLocalDate(p.measure_start_date);
    else if (p?.start_date_second) return toLocalDate(p.start_date_second);
    else return state?.startDate2;
  };

  const getSecondEndDate = p => {
    if (p?.measure_end_date) return toLocalDate(p.measure_end_date);
    else if (p?.end_date_second) return toLocalDate(p.end_date_second);
    else return state?.endDate2;
  };

  // edit mode hydrator: map only common fields
  const hydrateFrom = React.useCallback((incoming) => {
    if (!incoming) return;
    const p = incoming?.definition?.parameters || incoming?.parameters || {};
    const days = incoming?.definition?.parameters?.days || "";
    // Convert arrays back to CSV
    const toCsv = (arr) => Array.isArray(arr) ? arr.join(", ") : "";

    batch({
      priorQueryParams: incoming,

      fileName: incoming?.definition?.description ?? incoming?.description ?? state.fileName,
      geographyType: p?.geography_type ?? state.geographyType,
      payerMix: p?.payer_mix ?? state.payerMix,
      providerType: p?.providerType ?? state.providerType,
      reportType: p?.reportType ?? state.reportType,
      canBeVisualized: p?.canBeVisualized ?? state.canBeVisualized,

      zipCodes: toCsv(p?.zips) || state.zipCodes,
      cbsaSelection: p?.cbsa ? cbsa.filter(item => item.code === p.cbsa)[0] : state.cbsaSelection,
      stateSelection: p?.state ? states.filter(stateItem => stateItem.value === p.state)[0] : state.stateSelection,
      patientZip3s: toCsv(p?.patientZip3s) || state.patientZip3s,
      countiesSelection: p?.fips ? counties.filter((county) => p.fips.includes(county.code)).map((county) => county.code) : state.countiesSelection,

      startDate: getFirstStartDate(p),
      endDate: getFirstEndDate(p),
      startDate2:  getSecondStartDate(p),
      endDate2: getSecondEndDate(p),

      medicareRefDate: p?.medicare_ref_date ? p.medicare_ref_date : (p?.quarter) ? p.quarter : state.medicareRefDate,
      comboRefDate: p?.quarter ? p.quarter : state.comboRefDate,
      npis: toCsv(p?.providerlist ?? p?.referring_npis),
      npis2: toCsv(p?.rendering_npis),
      codeType: p?.codeoutput ?? p?.codetype ?? state.codeType,
      codeInput: p?.code_input ? p.code_input?.join(',') : state.codeInput,
      timeFrameDirection: days.slice(0, 1) === '-' ? "-" : "+",
      timeFrameDays: days.slice(0, 1) === '-' ? days.slice(1) : days.slice(),
      includeFacilities: p?.includeFacilities ? p.includeFacilities : state.includeFacilities
    });


    const outs = new Set(incoming?.definition?.outputFields || []);
    const has = (label) => outs.has(label);
    setOutput("npi", has("Physician NPI") || has("Organization NPI"));
    setOutput("name", has("Physician Name") || has("Organization Name"));
    setOutput("classification", has("Physician Classification") || has("Organization Classification"));
    setOutput("specialization", has("Physician Specialization") || has("Organization Specialization"));
    setOutput("icd10Cm", has("ICD-10-CM"));
    setOutput("address", has("Address"));

    setOutput("msdrg", has("msdrg"));
    setOutput("cptHcpcs", has("cptHcpcs"));

    setOutput("city", has("City"));
    setOutput("stateName", has("State"));
    setOutput("county", has("County"));
    setOutput("zipCode", has("ZIP Code"));
    setOutput("source", has("Source"));
    setOutput("distinctClaimsCount", has("Count of Distinct Claims"));
    setOutput("distinctPatientsCount", has("Count of Distinct Patients"));
    setOutput("proceduresCount", has("Count of Procedures"));
    setOutput("sumCharges", has("Sum of Charges"));
    setOutput("hospitalAffiliationIds", has("Hospital Affiliation IDs"));
    setOutput("hospitalAffiliationNames", has("Hospital Affiliation Names"));
    setOutput("groupPracticeIds", has("Group Practice IDs"));
    setOutput("groupPracticeNames", has("Group Practice Names"));
    setOutput("year", has("Year"));
    setOutput("quarter", has("Quarter"));

    setOutput("month", has("Month"));
    setOutput("sourceNpi", has("Source NPI"));
    setOutput("sourceName", has("Source Name"));
    setOutput("outputCode", has("CPT/HCPCS Code") || has('ICD-10-CM'));
    setOutput("codeDescription", has("Code Description"));
    setOutput("placeOfService", has("Place of Service"));
    setOutput("locationNpi", has("Location NPI"));
    setOutput("locationName", has("Location Name"));
    setOutput("locationClassification", has("Location Classification"));
    setOutput("locationSpecialization", has("Location Specialization"));

    setOutput("billingNpi", has("Billing NPI"));
    setOutput("billingName", has("Billing Name"));
    setOutput("billingClassification", has("Billing Classification"));
    setOutput("billingSpecialization", has("Billing Specialization"));

    setOutput("outputCodeType", has("outputCodeType"));
    setOutput("codeSet", has("Codeset"));

    setOutput("renderingNpi", has("Rendering NPI"));
    setOutput("renderingName", has("Rendering Name"));
    setOutput("renderingClassification", has("Rendering Classification"));
    setOutput("renderingSpecialization", has("Rendering Specialization"));
    setOutput("renderingState", has("Rendering State"));
    setOutput("renderingCity", has("Rendering City"));
    setOutput("renderingZipCode", has("Rendering ZIP Code"));
    setOutput("billingState", has("Billing State"));
    setOutput("billingCity", has("Billing City"));
    setOutput("billingZipCode", has("Billing ZIP Code"));

    setOutput("fipsCode", has("Patient FIPS County"));
    setOutput("fipsCountyName", has("Patient FIPS County Name"));
    setOutput("fipsState", has("Patient FIPS State"));

    setOutput("firstNPI", has("First NPI"));
    setOutput("firstPhysicianName", has("First Physician Name"));
    setOutput("firstPhysicianClassification", has("First Physician Classification"));
    setOutput("firstPhysicianSpecialization", has("First Physician Specialization"));
    setOutput("newPatients", has("New Patients"));
    setOutput("secondNPI", has("Second NPI"));
    setOutput("secondPhysicianName", has("Second Physician Name"));
    setOutput("secondPhysicianClassification", has("Second Physician Classification"));
    setOutput("secondPhysicianSpecialization", has("Second Physician Specialization"));
    setOutput("timeframe", has("Timeframe"));
    setOutput("procedureCode", has("Procedure Code"));
    setOutput("procedureDescription", has("Procedure Description"));
    setOutput("sharedPatients", has("Shared Patients"));
    setOutput("firstOrgNPI", has("First Organization NPI"));
    setOutput("firstOrgName", has("First Organization Name"));
    setOutput("firstOrgClassification", has("First Organization Classification"));
    setOutput("firstOrgSpecialization", has("First Organization Specialization"));
    setOutput("secondOrgNPI", has("Second Organization NPI"));
    setOutput("secondOrgName", has("Second Organization Name"));
    setOutput("secondOrgClassification", has("Second Organization Classification"));
    setOutput("secondOrgSpecialization", has("Second Organization Specialization"));
  }, [batch, setOutput, state]);

  const prev = React.useRef(state);
  React.useEffect(() => {
    if (!enableAutoClear) return;


    prev.current = state;
  }, [state, set, enableAutoClear]);

  return {
    state,
    set,
    setOutput,
    batch,
    bind,
    resetCommon,
    hydrateFrom,
    selectors,
  };
}
